import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Play, Calendar, Clock, Eye, Download, Share2, ChevronLeft, Sparkles } from 'lucide-react'
import { getMovieBySlug } from '../lib/supabase'
import MovieRow from '../components/MovieRow'

export default function MovieDetail({ session }) {
  const { slug } = useParams()
  const [movie, setMovie] = useState(null)
  const [related, setRelated] = useState([])
  const [loading, setLoading] = useState(true)
  const [showPlayer, setShowPlayer] = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const data = await getMovieBySlug(slug)
        setMovie(data)
        if (data.embed_url) setShowPlayer(true)
      } catch (e) {
        console.error('Load error:', e)
      } finally {
        setLoading(false)
      }
    }
    load()
    window.scrollTo(0, 0)
  }, [slug])

  if (loading) {
    return (
      <div className="pt-20 px-4 max-w-7xl mx-auto">
        <div className="aspect-video bg-dark-800 rounded-xl animate-pulse mb-8" />
        <div className="h-8 w-64 bg-dark-800 rounded animate-pulse mb-4" />
        <div className="h-4 w-full bg-dark-800 rounded animate-pulse mb-2" />
        <div className="h-4 w-3/4 bg-dark-800 rounded animate-pulse" />
      </div>
    )
  }

  if (!movie) {
    return (
      <div className="pt-20 px-4 text-center">
        <h2 className="text-2xl font-bold mb-4">Film tidak ditemukan</h2>
        <Link to="/" className="text-primary-500 hover:text-primary-400">Kembali ke Beranda</Link>
      </div>
    )
  }

  return (
    <div className="pt-16">
      {/* Backdrop */}
      <div className="relative h-[50vh] min-h-[300px]">
        {movie.backdrop_url && (
          <img src={movie.backdrop_url} alt={movie.title} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/40 to-dark-900/10" />
        <div className="absolute top-4 left-4 z-10">
          <Link to="/" className="flex items-center gap-2 px-3 py-2 bg-black/50 backdrop-blur-sm text-white rounded-lg text-sm hover:bg-black/70 transition-colors">
            <ChevronLeft className="w-4 h-4" /> Kembali
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 -mt-40 relative z-10 pb-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Poster */}
          <div className="w-48 md:w-64 flex-shrink-0">
            <div className="aspect-[2/3] rounded-xl overflow-hidden shadow-2xl border border-dark-700">
              {movie.poster_url ? (
                <img src={movie.poster_url} alt={movie.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-dark-800 flex items-center justify-center"><Play className="w-16 h-16 text-gray-600" /></div>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="px-3 py-1 bg-primary-500/20 text-primary-500 text-xs font-semibold rounded-full">{movie.type === 'series' ? 'Series' : 'Film'}</span>
              {movie.year && <span className="px-3 py-1 bg-dark-800 text-gray-300 text-xs rounded-full">{movie.year}</span>}
              {movie.duration && <span className="px-3 py-1 bg-dark-800 text-gray-300 text-xs rounded-full flex items-center gap-1"><Clock className="w-3 h-3" />{movie.duration}</span>}
            </div>

            <h1 className="text-3xl md:text-4xl font-black mb-4">{movie.title}</h1>

            {movie.genres && movie.genres.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {movie.genres.map((g) => (
                  <Link key={g} to={`/genre/${g.toLowerCase()}`} className="px-3 py-1 bg-dark-800 border border-dark-700 text-gray-300 text-sm rounded-lg hover:border-primary-500/50 transition-colors">{g}</Link>
                ))}
              </div>
            )}

            <p className="text-gray-300 leading-relaxed mb-6">{movie.description || 'Tidak ada deskripsi.'}</p>

            <div className="flex flex-wrap gap-3 mb-8">
              <button
                onClick={() => setShowPlayer(!showPlayer)}
                className="flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors group"
              >
                <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                {showPlayer ? 'Sembunyikan Player' : 'Putar'}
              </button>
              <button className="flex items-center gap-2 px-4 py-3 bg-dark-800 border border-dark-700 text-gray-300 rounded-lg hover:text-white hover:border-primary-500/50 transition-colors">
                <Download className="w-5 h-5" /> Unduh
              </button>
              <button className="flex items-center gap-2 px-4 py-3 bg-dark-800 border border-dark-700 text-gray-300 rounded-lg hover:text-white hover:border-primary-500/50 transition-colors">
                <Share2 className="w-5 h-5" /> Bagikan
              </button>
            </div>

            {/* Meta */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {movie.director && <div><span className="text-gray-500 block">Sutradara</span><span className="text-white">{movie.director}</span></div>}
              {movie.cast && <div><span className="text-gray-500 block">Pemain</span><span className="text-white">{movie.cast}</span></div>}
              {movie.country && <div><span className="text-gray-500 block">Negara</span><span className="text-white">{movie.country}</span></div>}
              {movie.rating && <div><span className="text-gray-500 block">Rating</span><span className="text-yellow-500 font-semibold">⭐ {movie.rating}</span></div>}
            </div>
          </div>
        </div>

        {/* Video Player */}
        {showPlayer && movie.embed_url && (
          <div className="mt-8 animate-fade-in">
            <h3 className="text-xl font-bold mb-4">Player</h3>
            <div className="aspect-video rounded-xl overflow-hidden bg-black border border-dark-700">
              <iframe
                src={movie.embed_url}
                className="w-full h-full"
                allowFullScreen
                allow="autoplay; fullscreen; picture-in-picture"
                title={movie.title}
              />
            </div>
          </div>
        )}

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-12">
            <h3 className="text-xl font-bold mb-4">Film Serupa</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {/* Related movies */}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
